import ScrollTrigger from 'gsap/ScrollTrigger';
import imagesLoaded from 'imagesloaded';
import debounce from 'lodash/debounce';
import Header from '../globals/header';
import { isIntroRunning, onIntroReveal } from '../globals/intro-state';

/**
 * Alle Komponenten, die pro Seite initialisiert werden.
 * Die Reihenfolge ist auch die Reihenfolge der Initialisierung
 * (wichtig für gepinnte ScrollTrigger).
 */
const COMPONENTS = [
	{ selector: '[data-stagger-in="el"]', load: () => import('../components/stagger-in') },
	{
		selector: '[data-project-slider="el"]',
		load: () => import('../components/project-slider'),
		onCreate: slider => {
			if (isIntroRunning()) {
				onIntroReveal(() => slider.setFirstItemActive());
			} else {
				setTimeout(() => slider.setFirstItemActive(), 250); // Slider kurz zur Ruhe kommen lassen
			}
		},
	},
	{ selector: '[data-sticky="el"]', load: () => import('../components/sticky') },
	{ selector: '[data-marquee="el"]', load: () => import('../components/marquee') },
	{ selector: '[data-s-marquee="el"]', load: () => import('../components/s-marquee') },
	{ selector: '[data-accordion="el"]', load: () => import('../components/accordion') },
	{ selector: '[data-scroll-story="el"]', load: () => import('../components/scroll-story') },
	{ selector: '[data-gallery="el"]', load: () => import('../components/gallery') },
	{ selector: '[data-filter="el"]', load: () => import('../components/filter') },
	{ selector: '[data-video="el"]', load: () => import('../components/video') },
	{ selector: '[data-video-player="el"]', load: () => import('../components/video-player') },
	{ selector: '[data-animation="el"]', load: () => import('../components/animation') },
	{ selector: '[data-dashboard="el"]', load: () => import('../components/dashboard') },
	{ selector: '[data-event-intro="el"]', load: () => import('../components/event-intro') },
	{ selector: '[data-countUp="el"]', load: () => import('../components/count-up') },
	{ selector: '[data-project-gallery="el"]', load: () => import('../components/project-gallery') },
	{ selector: '[data-hero-web="el"]', load: () => import('../components/hero-web') },
	{ selector: '[data-process="el"]', load: () => import('../components/process-scroll') },
	{ selector: '[data-tile-slider="el"]', load: () => import('../components/tile-slider') },
	{ selector: '[data-anchor-nav="el"]', load: () => import('../components/anchor-nav') },
];

/** Klassen auf <html>, die von Komponenten gesetzt werden und beim Seitenwechsel weg müssen */
const TRANSIENT_HTML_CLASSES = 'black process-pinned scroll-story-pinned';

class Default {
	$html = $('html');
	components = [];
	pageToken = null;

	beforeLeave() {}

	afterLeave(data) {
		// Noch ladende Komponenten der alten Seite verwerfen
		this.pageToken = null;

		this.destroyComponents();

		// Sicherheitsnetz: übrig gebliebene ScrollTrigger entfernen
		ScrollTrigger.getAll().forEach(trigger => trigger.kill());

		this.removeModifier(data.current.container);
		this.$html.removeClass(TRANSIENT_HTML_CLASSES);
	}

	beforeEnter(data) {
		window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
		const scope = data.next.container;

		this.initImagesLoaded(scope);
		this.setModifier(scope);

		// Beim ersten Seitenaufruf hat der Browser die Scripts bereits ausgeführt
		if (data.trigger !== 'barba') {
			this.executeInlineScripts(scope);
		}

		this.initComponents(scope);
	}

	afterEnter(data) {
		const scope = data.next.container;
		const sectionId = $(scope).data('section-id');
		Header.setActiveNavItem(sectionId); // Set active nav item based on section ID
		Header.closeMenu();
	}

	/* ------------------------------------------------------------------
	 * Komponenten
	 * ------------------------------------------------------------------ */

	initComponents(scope) {
		const pageToken = {};
		this.pageToken = pageToken;

		const found = COMPONENTS
			.map(definition => ({ ...definition, elements: scope.querySelectorAll(definition.selector) }))
			.filter(({ elements }) => elements.length);

		// Module parallel laden, aber in fester Reihenfolge initialisieren
		return Promise.all(
			found.map(definition =>
				definition.load().then(
					module => ({ ...definition, Component: module.default }),
					error => {
						console.error(`Komponente ${definition.selector} konnte nicht geladen werden`, error);
						return null;
					}
				)
			)
		).then(loaded => {
			// Seite wurde verlassen, bevor alles geladen war
			if (this.pageToken !== pageToken) return;

			loaded.filter(Boolean).forEach(({ Component, elements, onCreate, selector }) => {
				elements.forEach(el => {
					try {
						const instance = new Component($(el));
						this.components.push(instance);
						if (onCreate) onCreate(instance);
					} catch (error) {
						console.error(`Fehler beim Initialisieren von ${selector}`, error);
					}
				});
			});

			ScrollTrigger.sort();
			ScrollTrigger.refresh();
		});
	}

	destroyComponents() {
		this.components.forEach(component => {
			try {
				if (typeof component.destroy === 'function') component.destroy();
			} catch (error) {
				console.error('Fehler beim Aufräumen einer Komponente', error);
			}
		});
		this.components = [];
	}

	/* ------------------------------------------------------------------
	 * Hilfsfunktionen
	 * ------------------------------------------------------------------ */

	setModifier(scope) {
		const modifier = $(scope).data('modifier');
		if (modifier) {
			this.$html.addClass(modifier);
		}
	}

	removeModifier(scope) {
		const modifier = $(scope).data('modifier');
		if (modifier) {
			this.$html.removeClass(modifier);
		}
	}

	initImagesLoaded(scope) {
		// Nicht nach jedem einzelnen Bild neu berechnen, sondern gebündelt
		const refresh = debounce(() => ScrollTrigger.refresh(), 150);

		imagesLoaded(scope)
			.on('progress', refresh)
			.on('always', () => {
				refresh.cancel();
				ScrollTrigger.refresh();
			});
	}

	executeInlineScripts = container => {
		const executableTypes = ['', 'text/javascript', 'application/javascript', 'module'];
		const scripts = container.querySelectorAll('script');
		scripts.forEach(oldScript => {
			// JSON-LD, Templates usw. nicht als JavaScript ausführen
			const type = (oldScript.getAttribute('type') || '').trim().toLowerCase();
			if (!executableTypes.includes(type)) return;

			const newScript = document.createElement('script');
			// Alle Attribute übernehmen (type, src, async, nonce, data-* ...)
			[...oldScript.attributes].forEach(attr => newScript.setAttribute(attr.name, attr.value));
			if (!oldScript.src) {
				newScript.textContent = oldScript.textContent; // Inline Skript ausführen
			}
			document.body.appendChild(newScript);
			document.body.removeChild(newScript);
		});
	};
}

export default Default;
